import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * A custom element that displays recently viewed products
 */
class RecentlyViewedProducts extends HTMLElement {
  /**
   * The observer for the recently viewed products section
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-has-recently-viewed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-has-recently-viewed' &&
        this.dataset.hasRecentlyViewed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadProducts();
      break;
    }
  });

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    // In theme editor, always show the section structure
    if (Shopify.designMode) {
      this.dataset.hasRecentlyViewed = 'true';
      // Don't try to load products in theme editor
      return;
    }
    
    // Check if section is already visible on page load
    const rect = this.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight + 400;
    
    if (this.#shouldLog()) {
      console.log('[RecentlyViewed] Section connected, visible:', isVisible);
    }
    
    if (isVisible) {
      // Load immediately if already visible
      this.#loadProducts();
    }
    
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }
  
  /**
   * Check if debug logging should be enabled
   * @returns {boolean}
   */
  #shouldLog() {
    return window.location.search.includes('debug=recently-viewed');
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the recently viewed products
   */
  #loadProducts() {
    const { sectionId, productsCount } = this.dataset;
    const id = this.id;

    if (!id) {
      const error = 'ID attribute is required';
      if (this.#shouldLog()) console.error('[RecentlyViewed]', error);
      throw new Error(error);
    }

    // Get recently viewed product IDs from localStorage
    const viewedProducts = RecentlyViewed.getProducts();

    if (this.#shouldLog()) {
      console.log('[RecentlyViewed] Products in localStorage:', viewedProducts);
      console.log('[RecentlyViewed] Section ID:', sectionId);
      console.log('[RecentlyViewed] Element ID:', id);
    }

    if (viewedProducts.length === 0) {
      if (this.#shouldLog()) {
        console.log('[RecentlyViewed] No products in localStorage, hiding section');
      }
      this.dataset.hasRecentlyViewed = 'false';
      // In theme editor, still show the section structure
      if (Shopify.designMode) {
        const grid = this.querySelector('.js-recentPdpBlockGrid');
        if (grid instanceof HTMLElement) {
          grid.style.display = 'none';
        }
      }
      return;
    }

    // If the products have already been loaded
    if (this.dataset.hasRecentlyViewed === 'true') {
      if (this.#shouldLog()) {
        console.log('[RecentlyViewed] Products already loaded, skipping');
      }
      return;
    }

    if (this.#shouldLog()) {
      console.log('[RecentlyViewed] Starting to fetch products...');
    }

    this.#fetchProducts(sectionId, viewedProducts, productsCount)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            const error = new Error(`Server returned ${result.status}`);
            if (this.#shouldLog()) {
              console.error('[RecentlyViewed] Fetch failed:', error.message);
            }
            this.#handleError(error);
          }
          return;
        }

        if (this.#shouldLog()) {
          console.log('[RecentlyViewed] Section HTML received, length:', result.data?.length);
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recentlyViewedSection = html.querySelector(`recently-viewed[id="${id}"]`);

        if (this.#shouldLog()) {
          console.log('[RecentlyViewed] Found section element in response:', !!recentlyViewedSection);
          if (recentlyViewedSection) {
            console.log('[RecentlyViewed] Section innerHTML length:', recentlyViewedSection.innerHTML.trim().length);
          }
        }

        if (recentlyViewedSection?.innerHTML && recentlyViewedSection.innerHTML.trim().length) {
          this.dataset.hasRecentlyViewed = 'true';
          
          if (this.#shouldLog()) {
            console.log('[RecentlyViewed] Products loaded successfully, showing section');
          }
          
          // Extract the grid content from the fetched HTML
          const grid = recentlyViewedSection.querySelector('.js-recentPdpBlockGrid');
          const currentGrid = this.querySelector('.js-recentPdpBlockGrid');
          
          if (this.#shouldLog()) {
            console.log('[RecentlyViewed] Grid elements found - fetched:', !!grid, 'current:', !!currentGrid);
          }
          
          if (grid instanceof HTMLElement && currentGrid instanceof HTMLElement) {
            // Update the grid content while preserving the section structure
            currentGrid.innerHTML = grid.innerHTML;
            // Update data-has-products attribute if present
            if (grid.dataset.hasProducts) {
              currentGrid.dataset.hasProducts = grid.dataset.hasProducts;
            }
            if (this.#shouldLog()) {
              console.log('[RecentlyViewed] Grid content updated successfully');
            }
          } else {
            // Fallback: replace entire innerHTML
            this.innerHTML = recentlyViewedSection.innerHTML;
            if (this.#shouldLog()) {
              console.log('[RecentlyViewed] Used fallback: replaced entire innerHTML');
            }
          }
        } else {
          if (this.#shouldLog()) {
            console.warn('[RecentlyViewed] Section HTML is empty or invalid, hiding section');
          }
          this.dataset.hasRecentlyViewed = 'false';
        }
      })
      .catch((e) => {
        if (this.#shouldLog()) {
          console.error('[RecentlyViewed] Error in promise chain:', e);
        }
        this.#handleError(e);
      });
  }

  /**
   * Fetches the products using the search API
   * @param {string | undefined} sectionId
   * @param {string[]} productIds
   * @param {string | undefined} productsCount
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchProducts(sectionId, productIds, productsCount) {
    if (!sectionId) {
      if (this.#shouldLog()) {
        console.error('[RecentlyViewed] Section ID is missing');
      }
      return { success: false, status: 400 };
    }

    // Build search URL with product IDs
    const url = new URL(Theme.routes.search_url, location.origin);
    const searchQuery = productIds.map((id) => `id:${id}`).join(' OR ');
    url.searchParams.set('q', searchQuery);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', productsCount || '8');

    if (this.#shouldLog()) {
      console.log('[RecentlyViewed] Search URL:', url.toString());
      console.log('[RecentlyViewed] Search query:', searchQuery);
      console.log('[RecentlyViewed] Product IDs:', productIds);
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false, url);
      
      if (this.#shouldLog()) {
        console.log('[RecentlyViewed] Section HTML fetched successfully, length:', sectionHTML?.length);
      }
      
      return { success: true, data: sectionHTML };
    } catch (error) {
      if (this.#shouldLog()) {
        console.error('[RecentlyViewed] Fetch error:', error);
      }
      if (error.name === 'AbortError') {
        return { success: false, status: 0 };
      }
      return { success: false, status: 500 };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('[RecentlyViewed] Error:', error.message);
    if (this.#shouldLog()) {
      console.error('[RecentlyViewed] Full error:', error);
    }
    this.dataset.hasRecentlyViewed = 'false';
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedProducts);
}
